import * as jsondiffpatch from 'jsondiffpatch'
import type { Config } from '@/types/config'
import { stripHtml } from '@/services/string'
import api from '@/services/axiosApi'
import { isAQuestion } from '@/modules/segment/services/segmentUtilities'

export interface ConfigChange {
  category: string
  type: 'added' | 'modified' | 'deleted'
  name: string
  id: string
  details?: string[] // Array of specific changes like "Title changed", "Added 2 segments"
  diff?: any // The actual jsondiffpatch diff for this item (for AI analysis)
}

interface SplitTest {
  pid: string
  resource_type: string
  resource_id: string
  status: string
  variants: any[]
  created_at: string
  updated_at: string
  started_at: string | null
}

interface Integration {
  name: string
  label: string
  hasExistingIntegration: boolean
  lastPingAt?: string | null
  created_at?: string
  updated_at?: string
}

interface IntegrationConnection {
  provider_name: string
  created_at: string
  updated_at: string
}

// Module-level cache (shared across all component instances)
const cachedSplitTests = ref<SplitTest[]>([])
const cachedIntegrations = ref<{ v1: Integration[], v2: IntegrationConnection[] }>({ v1: [], v2: [] })
const splitTestsLastFetched = ref<number>(0)
const integrationsLastFetched = ref<number>(0)
const splitTestsFetched = ref(false) // Track if we've fetched at all
const integrationsFetched = ref(false) // Track if we've fetched at all
const CACHE_DURATION = 10000 // 10 seconds - long enough to avoid rapid refetches during editing

// Track pending fetches to avoid duplicate requests (module-level)
let pendingSplitTestsFetch: Promise<SplitTest[]> | null = null
let pendingIntegrationsFetch: Promise<{ v1: Integration[], v2: IntegrationConnection[] }> | null = null

export function useConfigChanges() {
  const configStore = useConfigStore()
  const { currentTeamPid, currentTeam } = storeToRefs(configStore)

  // Invalidate cache when team changes (only if currentTeamPid is defined)
  watch(
    () => currentTeamPid?.value,
    () => {
      cachedSplitTests.value = []
      cachedIntegrations.value = { v1: [], v2: [] }
      splitTestsLastFetched.value = 0
      integrationsLastFetched.value = 0
      splitTestsFetched.value = false
      integrationsFetched.value = false
      pendingSplitTestsFetch = null
      pendingIntegrationsFetch = null
    }
  )

  // Fetch active split tests from the API with caching
  const fetchActiveSplitTests = async (): Promise<SplitTest[]> => {
    const now = Date.now()

    // Return cached data if still valid (check if fetched, not if data exists)
    if (splitTestsFetched.value && (now - splitTestsLastFetched.value) < CACHE_DURATION) {
      return cachedSplitTests.value
    }

    // Return pending fetch if one is in progress
    if (pendingSplitTestsFetch) {
      return pendingSplitTestsFetch
    }

    // Start new fetch
    if (!currentTeamPid.value) return []

    pendingSplitTestsFetch = (async () => {
      try {
        const response = await api.get<SplitTest[]>(`/api/${currentTeamPid.value}/splitTests`)
        const tests = response.data.filter(test => test.status === 'active')
        cachedSplitTests.value = tests
        splitTestsLastFetched.value = Date.now()
        splitTestsFetched.value = true
        return tests
      } catch (error) {
        console.error('Error fetching split tests:', error)
        return []
      } finally {
        pendingSplitTestsFetch = null
      }
    })()

    return pendingSplitTestsFetch
  }

  // Get a human-readable name for a split test resource
  const getSplitTestResourceName = (test: SplitTest): string => {
    const resourceType = test.resource_type === 'offer' ? 'Offer' : 'Question'

    // Try to get the actual resource name from stores
    if (test.resource_type === 'offer') {
      const offerStore = useOfferStore()
      const offer = offerStore.getById(test.resource_id)
      return offer?.name || 'Unknown offer'
    } else {
      const questionStore = useQuestionStore()
      const segmentGroupStore = useSegmentGroupStore()
      const question = questionStore.getById(test.resource_id)
      if (question?.dimensionId) {
        const segmentGroup = segmentGroupStore.getById(question.dimensionId)
        return segmentGroup?.name || 'Unknown question'
      }
      return 'Unknown question'
    }
  }

  // Check if split tests have changed since last publish
  const checkSplitTestChanges = async (publishedConfig: Config | null): Promise<ConfigChange[]> => {
    const changes: ConfigChange[] = []

    // Get current active split tests
    const activeSplitTests = await fetchActiveSplitTests()

    // Get the last published timestamp from the team
    const lastPublishedAt = configStore.currentTeam?.last_published_at

    if (!lastPublishedAt) {
      // If never published, all active tests need publishing
      activeSplitTests.forEach(test => {
        const resourceType = test.resource_type === 'offer' ? 'Offer' : 'Question'
        const resourceName = getSplitTestResourceName(test)
        changes.push({
          category: 'Split Tests',
          type: 'added',
          name: `Testing ${resourceType}: ${resourceName}`,
          id: test.pid
        })
      })
      return changes
    }

    // Compare each active test's timestamps with last publish time
    const lastPublished = new Date(lastPublishedAt)

    activeSplitTests.forEach(test => {
      // Check if test was created after last publish
      const createdAt = new Date(test.created_at)
      const updatedAt = new Date(test.updated_at)
      const startedAt = test.started_at ? new Date(test.started_at) : null

      // Test needs publishing if:
      // 1. Created after last publish, OR
      // 2. Started after last publish (test was activated), OR
      // 3. Updated after last publish (variants changed)
      const needsPublish =
        createdAt > lastPublished ||
        (startedAt && startedAt > lastPublished) ||
        updatedAt > lastPublished

      if (needsPublish) {
        const resourceType = test.resource_type === 'offer' ? 'Offer' : 'Question'
        const resourceName = getSplitTestResourceName(test)

        // Determine if it's new or modified
        const isNew = createdAt > lastPublished

        changes.push({
          category: 'Split Tests',
          type: isNew ? 'added' : 'modified',
          name: `Testing ${resourceType}: ${resourceName}`,
          id: test.pid,
          details: isNew ? undefined : ['Test configuration changed']
        })
      }
    })

    return changes
  }

  // Fetch integrations from the API (both V1 and V2) with caching
  const fetchIntegrations = async (): Promise<{ v1: Integration[], v2: IntegrationConnection[] }> => {
    const now = Date.now()

    // Return cached data if still valid (check if fetched, not if data exists)
    if (integrationsFetched.value && (now - integrationsLastFetched.value) < CACHE_DURATION) {
      return cachedIntegrations.value
    }

    // Return pending fetch if one is in progress
    if (pendingIntegrationsFetch) {
      return pendingIntegrationsFetch
    }

    // Start new fetch
    if (!currentTeamPid.value) return { v1: [], v2: [] }

    pendingIntegrationsFetch = (async () => {
      try {
        const team = configStore.currentTeam
        const usesV2 = team?.uses_v2_integrations

        let result: { v1: Integration[], v2: IntegrationConnection[] }

        // Fetch V2 integrations
        if (usesV2) {
          const response = await api.get(`/api/${currentTeamPid.value}/connections`)
          result = { v1: [], v2: response.data.partners || [] }
        } else {
          // Fetch V1 integrations
          const response = await api.get(`/api/${currentTeamPid.value}/integrations/partners`)
          const partners = response.data.partners || []

          // Filter to only integrations that exist and have timestamps
          const existingIntegrations = partners.filter((p: Integration) => p.hasExistingIntegration)

          result = { v1: existingIntegrations, v2: [] }
        }

        cachedIntegrations.value = result
        integrationsLastFetched.value = Date.now()
        integrationsFetched.value = true
        return result
      } catch (error) {
        console.error('Error fetching integrations:', error)
        return { v1: [], v2: [] }
      } finally {
        pendingIntegrationsFetch = null
      }
    })()

    return pendingIntegrationsFetch
  }

  // Check if integrations have changed since last publish
  const checkIntegrationChanges = async (publishedConfig: Config | null): Promise<ConfigChange[]> => {
    const changes: ConfigChange[] = []

    // Get current integrations
    const { v1, v2 } = await fetchIntegrations()

    // Get the last published timestamp from the team
    const lastPublishedAt = configStore.currentTeam?.last_published_at

    if (!lastPublishedAt) {
      // If never published, all integrations need publishing
      v1.forEach(integration => {
        changes.push({
          category: 'Integrations',
          type: 'added',
          name: `${integration.label} integration`,
          id: integration.name
        })
      })

      v2.forEach(connection => {
        changes.push({
          category: 'Integrations',
          type: 'added',
          name: `${connection.provider_name} integration`,
          id: connection.provider_name
        })
      })

      return changes
    }

    const lastPublished = new Date(lastPublishedAt)

    // Check V1 integrations
    v1.forEach(integration => {
      if (!integration.created_at || !integration.updated_at) return

      const createdAt = new Date(integration.created_at)
      const updatedAt = new Date(integration.updated_at)

      // Integration needs publishing if created or updated after last publish
      const needsPublish = createdAt > lastPublished || updatedAt > lastPublished

      if (needsPublish) {
        const isNew = createdAt > lastPublished

        changes.push({
          category: 'Integrations',
          type: isNew ? 'added' : 'modified',
          name: `${integration.label} integration`,
          id: integration.name,
          details: isNew ? undefined : ['Integration settings changed']
        })
      }
    })

    // Check V2 integrations
    v2.forEach(connection => {
      const createdAt = new Date(connection.created_at)
      const updatedAt = new Date(connection.updated_at)

      // Integration needs publishing if created or updated after last publish
      const needsPublish = createdAt > lastPublished || updatedAt > lastPublished

      if (needsPublish) {
        const isNew = createdAt > lastPublished

        changes.push({
          category: 'Integrations',
          type: isNew ? 'added' : 'modified',
          name: `${connection.provider_name} integration`,
          id: connection.provider_name,
          details: isNew ? undefined : ['Integration settings changed']
        })
      }
    })

    return changes
  }

  // Check if team settings (like Turnstile keys) have changed since last publish
  const checkTeamSettingsChanges = (publishedConfig: Config | null): ConfigChange[] => {
    const changes: ConfigChange[] = []

    // Get current team
    const team = currentTeam.value
    if (!team) return changes

    // For now, only track Turnstile configuration changes
    // NOTE: We intentionally don't use team.updated_at because it changes for many
    // reasons unrelated to settings that need publishing (analytics, metadata, etc.)
    // Instead, we would need to track specific field changes in the future.

    // If this is the first publish and Turnstile is configured, report it
    const lastPublishedAt = team.last_published_at
    if (!lastPublishedAt && (team.recaptcha_key || team.recaptcha_secret)) {
      changes.push({
        category: 'Bot Protection',
        type: 'added',
        name: 'Cloudflare Turnstile configuration',
        id: 'team.turnstile'
      })
    }

    // TODO: For detecting changes after first publish, we would need to:
    // 1. Store previous Turnstile key hashes in published config, or
    // 2. Add a team_settings table with timestamps for specific settings, or
    // 3. Track specific field changes through audit log
    // For now, we don't detect Turnstile changes after initial publish to avoid
    // false positives from unrelated team.updated_at changes.

    return changes
  }

  // Helper function to check if diff only contains accessedAt changes
  const isOnlyAccessedAtChanges = (diff: any): boolean => {
    // If diff is null or undefined, there are no changes
    if (!diff) return true

    // Function to recursively check objects
    const checkObject = (obj: any): boolean => {
      // If it's not an object or is null, it's not an accessedAt change
      if (!obj || typeof obj !== 'object') return false

      // Check each key in the object
      for (const key in obj) {
        // Skip the _t property which is used by jsondiffpatch for arrays
        if (key === '_t') continue

        const value = obj[key]

        // If the key is 'accessedAt' and it's an array with 2 elements (old and new value)
        if (
          key === 'accessedAt' &&
          Array.isArray(value) &&
          value.length === 2
        ) {
          continue // This is an accessedAt change, which is fine
        } else if (typeof value === 'object' && value !== null) {
          // Recursively check nested objects
          if (!checkObject(value)) return false
        } else {
          // Found a non-accessedAt change
          return false
        }
      }

      // If we got here, all changes were accessedAt or contained objects with only accessedAt changes
      return true
    }

    return checkObject(diff)
  }

  // Check if two configs have meaningful differences (excluding accessedAt)
  const hasChanges = (currentConfig: Config | null, publishedConfig: Config | null): boolean => {
    if (!currentConfig || !publishedConfig) {
      return !!currentConfig !== !!publishedConfig
    }

    const diff = jsondiffpatch.diff(toRaw(publishedConfig), toRaw(currentConfig))
    if (!diff) return false

    return !isOnlyAccessedAtChanges(diff)
  }

  // Helper to strip HTML from diff objects for cleaner AI analysis
  const stripHtmlFromDiff = (diff: any): any => {
    if (!diff || typeof diff !== 'object') return diff

    // Handle arrays (jsondiffpatch format)
    if (Array.isArray(diff)) {
      return diff.map(item => {
        if (typeof item === 'string') {
          return stripHtml(item)
        }
        return item
      })
    }

    // Handle objects
    const cleaned: any = {}
    for (const key in diff) {
      const value = diff[key]
      if (typeof value === 'string') {
        cleaned[key] = stripHtml(value)
      } else if (typeof value === 'object') {
        cleaned[key] = stripHtmlFromDiff(value)
      } else {
        cleaned[key] = value
      }
    }
    return cleaned
  }

  // Helper to extract what fields changed in a modified object
  const getModifiedFields = (diff: any): string[] => {
    if (!diff || typeof diff !== 'object') return []

    const changes: string[] = []
    const fieldLabels: Record<string, string> = {
      name: 'Name',
      question: 'Question',
      text: 'Question text',
      segments: 'Segments',
      signals: 'Signals',
      syncTo: 'ESP sync',
      syncToCustomField: 'Custom field',
      syncToField: 'Sync field',
      parts: 'Content',
      title: 'Title',
      description: 'Description',
      buttonText: 'Button text',
      triggeredAction: 'Action',
      funnelDefinition: 'Flow structure',
      is_active: 'Active status',
      testing: 'A/B test settings',
      variants: 'Variants',
      options: 'Answer options',
      displayType: 'Display type',
      displayOptions: 'Display options',
      otherOptions: 'Other option settings',
      isRandomizingOptionsOrder: 'Randomize options',
      inputType: 'Input type',
      inputSettings: 'Input settings',
      dimensionId: 'Linked dimension',
      visibility: 'Visibility rules',
      isEnabled: 'Enabled status',
      // Attribution fields
      destination: 'Sync destination',
      overwrite: 'Overwrite setting',
      label: 'Label',
      value: 'Value template',
      internal: 'Internal flag'
    }

    // Fields to completely ignore
    const ignoredFields = ['accessedAt']

    for (const key in diff) {
      if (key === '_t') continue // Skip jsondiffpatch metadata
      if (ignoredFields.includes(key)) continue // Skip ignored fields

      const label = fieldLabels[key] || key
      const value = diff[key]

      if (Array.isArray(value)) {
        // jsondiffpatch array format:
        // [newValue] = added
        // [oldValue, newValue] = modified
        // [oldValue, 0, 0] = deleted
        if (value.length === 1) {
          // Added field
          changes.push(label)
        } else if (value.length === 2) {
          // Modified field
          changes.push(label)
        } else if (value.length === 3 && value[1] === 0 && value[2] === 0) {
          // Deleted field
          changes.push(label)
        }
      } else if (typeof value === 'object' && !Array.isArray(value)) {
        // Nested changes - recurse
        const nestedChanges = getModifiedFields(value)
        if (nestedChanges.length > 0) {
          changes.push(label)
        }
      }
    }

    return changes
  }

  const analyzeChanges = async (currentConfig: Config, publishedConfig: Config | null): Promise<ConfigChange[]> => {
    if (!publishedConfig) {
      // First publish - everything is new
      return analyzeNewConfig(currentConfig)
    }

    const diff = jsondiffpatch.diff(publishedConfig, currentConfig)

    const configChanges = diff ? parseConfigDiff(diff, currentConfig, publishedConfig) : []

    // Check for split test changes
    const splitTestChanges = await checkSplitTestChanges(publishedConfig)

    // Check for integration changes
    const integrationChanges = await checkIntegrationChanges(publishedConfig)

    // Check for team settings changes
    const teamSettingsChanges = checkTeamSettingsChanges(publishedConfig)

    return [...configChanges, ...splitTestChanges, ...integrationChanges, ...teamSettingsChanges]
  }

  const analyzeNewConfig = (config: Config): ConfigChange[] => {
    const changes: ConfigChange[] = []

    // Check for team settings (like Turnstile) on first publish
    const teamSettingsChanges = checkTeamSettingsChanges(null)
    changes.push(...teamSettingsChanges)

    // Count dimensions/segment groups (exclude questions)
    if (config.dimensions?.length > 0) {
      config.dimensions.forEach(dim => {
        // Use isAQuestion utility to properly determine if this is a question
        if (!isAQuestion(dim)) {
          changes.push({
            category: 'Segment Groups',
            type: 'added',
            name: dim.name || 'Unnamed Segment Group',
            id: dim.id
          })
        }
      })
    }

    // Count campaigns
    if (config.campaigns?.length > 0) {
      config.campaigns.forEach(campaign => {
        changes.push({
          category: 'Campaigns',
          type: 'added',
          name: campaign.name || 'Unnamed Campaign',
          id: campaign.id
        })
      })
    }

    // Count offers
    if (config.rightCta?.offers?.length > 0) {
      config.rightCta.offers.forEach(offer => {
        changes.push({
          category: 'Offers / Forms',
          type: 'added',
          name: offer.name || 'Unnamed Offer',
          id: offer.id
        })
      })
    }

    // Count flows
    if (config.rightCta?.offerFunnels?.length > 0) {
      config.rightCta.offerFunnels.forEach(flow => {
        changes.push({
          category: 'Flows',
          type: 'added',
          name: flow.name || 'Unnamed Flow',
          id: flow.id
        })
      })
    }

    // Count widgets
    if (config.rightCta?.widgets?.length > 0) {
      config.rightCta.widgets.forEach(widget => {
        changes.push({
          category: 'Widgets',
          type: 'added',
          name: widget.name || 'Unnamed Widget',
          id: widget.id
        })
      })
    }

    // Count goals
    if (config.goals?.length > 0) {
      config.goals.forEach(goal => {
        changes.push({
          category: 'Goals',
          type: 'added',
          name: goal.name || 'Unnamed Goal',
          id: goal.id
        })
      })
    }

    // Count questions
    if (config.rightAsk?.questions?.length > 0) {
      config.rightAsk.questions.forEach(question => {
        changes.push({
          category: 'Questions',
          type: 'added',
          name: question.text || 'Unnamed Question',
          id: question.id
        })
      })
    }

    return changes
  }

  const parseConfigDiff = (diff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
    const changes: ConfigChange[] = []

    // Check dimensions
    if (diff.dimensions) {
      changes.push(...parseDimensionsChanges(diff.dimensions, currentConfig, publishedConfig))
    }

    // Check campaigns
    if (diff.campaigns) {
      changes.push(...parseCampaignsChanges(diff.campaigns, currentConfig, publishedConfig))
    }

    // Handle rightCta - could be entirely new or have nested changes
    if (diff.rightCta) {
      // If rightCta is entirely new (array format from jsondiffpatch)
      if (Array.isArray(diff.rightCta)) {
        // The entire rightCta object was added - check current config for what's in it
        if (currentConfig.rightCta?.offers?.length > 0) {
          currentConfig.rightCta.offers.forEach(offer => {
            changes.push({
              category: 'Offers / Forms',
              type: 'added',
              name: offer.name || 'Unnamed Offer',
              id: offer.id
            })
          })
        }
        if (currentConfig.rightCta?.offerFunnels?.length > 0) {
          currentConfig.rightCta.offerFunnels.forEach(flow => {
            changes.push({
              category: 'Flows',
              type: 'added',
              name: flow.name || 'Unnamed Flow',
              id: flow.id
            })
          })
        }
        if (currentConfig.rightCta?.widgets?.length > 0) {
          currentConfig.rightCta.widgets.forEach(widget => {
            changes.push({
              category: 'Widgets',
              type: 'added',
              name: widget.name || 'Unnamed Widget',
              id: widget.id
            })
          })
        }
      } else {
        // rightCta exists but has nested changes
        if (diff.rightCta.offers) {
          changes.push(...parseOffersChanges(diff.rightCta.offers, currentConfig, publishedConfig))
        }
        if (diff.rightCta.offerFunnels) {
          changes.push(...parseFlowsChanges(diff.rightCta.offerFunnels, currentConfig, publishedConfig))
        }
        if (diff.rightCta.widgets) {
          changes.push(...parseWidgetsChanges(diff.rightCta.widgets, currentConfig, publishedConfig))
        }
        // Check for rightCta.settings changes (powered by badge, widget colors, etc.)
        if (diff.rightCta.settings) {
          changes.push({
            category: 'Widget Settings',
            type: 'modified',
            name: 'Widget configuration settings',
            id: 'rightCta.settings'
          })
        }
      }
    }

    // Check goals
    if (diff.goals) {
      changes.push(...parseGoalsChanges(diff.goals, currentConfig, publishedConfig))
    }

    // Check phrases
    if (diff.phrases) {
      changes.push(...parsePhrasesChanges(diff.phrases, currentConfig, publishedConfig))
    }

    // Check rightAsk (questions)
    if (diff.rightAsk) {
      changes.push(...parseQuestionsChanges(diff.rightAsk, currentConfig, publishedConfig))
    }

    // Check settings - only report if it's an actual change, not just being added for the first time
    if (diff.settings && !Array.isArray(diff.settings)) {
      // Check if it's just empty jsHooks being initialized (not a meaningful change)
      const isOnlyEmptyJsHooks = diff.settings.jsHooks && Object.keys(diff.settings).length === 1 &&
        Object.values(diff.settings.jsHooks).every(value => {
          // Check if it's an added empty string: [""]
          return Array.isArray(value) && value.length === 1 && value[0] === ''
        })

      if (!isOnlyEmptyJsHooks) {
        changes.push({
          category: 'Settings',
          type: 'modified',
          name: 'Configuration settings',
          id: 'settings'
        })
      }
    }

    // Check attributes (lead attribution settings)
    if (diff.attributes) {
      changes.push(...parseAttributesChanges(diff.attributes, currentConfig, publishedConfig))
    }

    return changes
  }

  const parseDimensionsChanges = (dimensionsDiff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
    const changes: ConfigChange[] = []

    if (dimensionsDiff._t === 'a') {
      // Array diff - use ID-based matching instead of index-based to avoid false positives
      const currentDimensions = currentConfig.dimensions || []
      const publishedDimensions = publishedConfig.dimensions || []

      // Create maps for efficient lookup
      const currentById = new Map(currentDimensions.map(d => [d.id, d]))
      const publishedById = new Map(publishedDimensions.map(d => [d.id, d]))

      // Find truly added dimensions
      currentDimensions.forEach(dim => {
        if (!publishedById.has(dim.id) && !isAQuestion(dim)) {
          changes.push({
            category: 'Segment Groups',
            type: 'added',
            name: dim.name || 'Unnamed Segment Group',
            id: dim.id
          })
        }
      })

      // Find truly deleted dimensions
      publishedDimensions.forEach(dim => {
        if (!currentById.has(dim.id) && !isAQuestion(dim)) {
          changes.push({
            category: 'Segment Groups',
            type: 'deleted',
            name: dim.name || 'Unnamed Segment Group',
            id: dim.id
          })
        }
      })

      // Find truly modified dimensions (by comparing actual content, not index)
      currentDimensions.forEach(currentDim => {
        const publishedDim = publishedById.get(currentDim.id)
        if (publishedDim) {
          // Compare the actual objects to see if there are real changes
          const diff = jsondiffpatch.diff(publishedDim, currentDim)
          if (diff) {
            const modifiedFields = getModifiedFields(diff)
            if (modifiedFields.length > 0) {
              // Use isAQuestion utility to properly determine if this is a question or segment group
              const hasValidQuestion = isAQuestion(currentDim)

              // Get the display name - use question text if it's a question, otherwise dimension name
              const linkedQuestion = currentConfig.rightAsk?.questions?.find(q => q.dimensionId === currentDim.id)
              const displayName = hasValidQuestion && linkedQuestion?.text
                ? linkedQuestion.text
                : (currentDim.name || 'Unnamed Segment Group')

              changes.push({
                category: hasValidQuestion ? 'Questions' : 'Segment Groups',
                type: 'modified',
                name: displayName,
                id: currentDim.id,
                details: modifiedFields,
                diff: diff
              })
            }
          }
        }
      })
    } else {
      // Handle non-array-diff format
      const currentDimensions = currentConfig.dimensions || []
      const publishedDimensions = publishedConfig.dimensions || []

      if (currentDimensions.length > publishedDimensions.length) {
        currentDimensions.forEach((dim) => {
          const existsInPublished = publishedDimensions.some(pd => pd.id === dim.id)
          if (!existsInPublished && !isAQuestion(dim)) {
            changes.push({
              category: 'Segment Groups',
              type: 'added',
              name: dim.name || 'Unnamed Segment Group',
              id: dim.id
            })
          }
        })
      }
    }

    return changes
  }

  const parseCampaignsChanges = (campaignsDiff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
    const changes: ConfigChange[] = []

    if (campaignsDiff._t === 'a') {
      Object.keys(campaignsDiff).forEach(key => {
        if (key === '_t') return

        const value = campaignsDiff[key]
        const index = parseInt(key.replace('_', ''))

        if (Array.isArray(value)) {
          if (value.length === 3 && value[1] === 0 && value[2] === 0) {
            // Deleted
            const item = publishedConfig.campaigns?.[index]
            if (item) {
              changes.push({
                category: 'Campaigns',
                type: 'deleted',
                name: item.name || 'Unnamed Campaign',
                id: item.id
              })
            }
          } else if (value.length === 1) {
            // Added
            const item = currentConfig.campaigns?.[index]
            if (item) {
              changes.push({
                category: 'Campaigns',
                type: 'added',
                name: item.name || 'Unnamed Campaign',
                id: item.id
              })
            }
          } else if (value.length === 2) {
            // Modified
            const item = currentConfig.campaigns?.[index]
            if (item) {
              const modifiedFields = getModifiedFields(value[1])
              changes.push({
                category: 'Campaigns',
                type: 'modified',
                name: item.name || 'Unnamed Campaign',
                id: item.id,
                details: modifiedFields.length > 0 ? modifiedFields : undefined,
                diff: value // [oldValue, newValue]
              })
            }
          }
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          const item = currentConfig.campaigns?.[index]
          if (item) {
            const modifiedFields = getModifiedFields(value)
            if (modifiedFields.length > 0) {
              changes.push({
                category: 'Campaigns',
                type: 'modified',
                name: item.name || 'Unnamed Campaign',
                id: item.id,
                details: modifiedFields,
                diff: value
              })
            }
          }
        }
      })
    } else {
      // Handle non-array-diff format
      const currentCampaigns = currentConfig.campaigns || []
      const publishedCampaigns = publishedConfig.campaigns || []

      if (currentCampaigns.length > publishedCampaigns.length) {
        currentCampaigns.forEach((campaign) => {
          const existsInPublished = publishedCampaigns.some(pc => pc.id === campaign.id)
          if (!existsInPublished) {
            changes.push({
              category: 'Campaigns',
              type: 'added',
              name: campaign.name || 'Unnamed Campaign',
              id: campaign.id
            })
          }
        })
      }
    }

    return changes
  }

  const parseOffersChanges = (offersDiff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
    const changes: ConfigChange[] = []

    if (offersDiff._t === 'a') {
      Object.keys(offersDiff).forEach(key => {
        if (key === '_t') return

        const value = offersDiff[key]
        const index = parseInt(key.replace('_', ''))

        if (Array.isArray(value)) {
          if (value.length === 3 && value[1] === 0 && value[2] === 0) {
            const item = publishedConfig.rightCta?.offers?.[index]
            if (item) {
              changes.push({
                category: 'Offers / Forms',
                type: 'deleted',
                name: item.name || 'Unnamed Offer',
                id: item.id
              })
            }
          } else if (value.length === 1) {
            const item = currentConfig.rightCta?.offers?.[index]
            if (item) {
              changes.push({
                category: 'Offers / Forms',
                type: 'added',
                name: item.name || 'Unnamed Offer',
                id: item.id
              })
            }
          } else if (value.length === 2) {
            const item = currentConfig.rightCta?.offers?.[index]
            if (item) {
              const modifiedFields = getModifiedFields(value[1])
              changes.push({
                category: 'Offers / Forms',
                type: 'modified',
                name: item.name || 'Unnamed Offer',
                id: item.id,
                details: modifiedFields.length > 0 ? modifiedFields : undefined,
                diff: value
              })
            }
          }
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          const item = currentConfig.rightCta?.offers?.[index]
          if (item) {
            const modifiedFields = getModifiedFields(value)
            if (modifiedFields.length > 0) {
              changes.push({
                category: 'Offers / Forms',
                type: 'modified',
                name: item.name || 'Unnamed Offer',
                id: item.id,
                details: modifiedFields,
                diff: value
              })
            }
          }
        }
      })
    } else {
      const currentOffers = currentConfig.rightCta?.offers || []
      const publishedOffers = publishedConfig.rightCta?.offers || []

      if (currentOffers.length > publishedOffers.length) {
        currentOffers.forEach((offer) => {
          const existsInPublished = publishedOffers.some(po => po.id === offer.id)
          if (!existsInPublished) {
            changes.push({
              category: 'Offers / Forms',
              type: 'added',
              name: offer.name || 'Unnamed Offer',
              id: offer.id
            })
          }
        })
      }
    }

    return changes
  }

  const parseFlowsChanges = (flowsDiff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
    const changes: ConfigChange[] = []

    if (flowsDiff._t === 'a') {
      Object.keys(flowsDiff).forEach(key => {
        if (key === '_t') return

        const value = flowsDiff[key]
        const index = parseInt(key.replace('_', ''))

        // jsondiffpatch array format:
        // [newValue] = added (length 1)
        // [oldValue, newValue] = modified (length 2)
        // [oldValue, 0, 0] = deleted (length 3)

        if (Array.isArray(value)) {
          if (value.length === 3 && value[1] === 0 && value[2] === 0) {
            // Deleted
            const item = publishedConfig.rightCta?.offerFunnels?.[index]
            if (item) {
              changes.push({
                category: 'Flows',
                type: 'deleted',
                name: item.name || 'Unnamed Flow',
                id: item.id
              })
            }
          } else if (value.length === 1) {
            // Added
            const item = currentConfig.rightCta?.offerFunnels?.[index]
            if (item) {
              changes.push({
                category: 'Flows',
                type: 'added',
                name: item.name || 'Unnamed Flow',
                id: item.id
              })
            }
          } else if (value.length === 2) {
            // Modified
            const item = currentConfig.rightCta?.offerFunnels?.[index]
            if (item) {
              const modifiedFields = getModifiedFields(value[1])
              changes.push({
                category: 'Flows',
                type: 'modified',
                name: item.name || 'Unnamed Flow',
                id: item.id,
                details: modifiedFields.length > 0 ? modifiedFields : undefined,
                diff: value
              })
            }
          }
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          const item = currentConfig.rightCta?.offerFunnels?.[index]
          if (item) {
            const modifiedFields = getModifiedFields(value)
            if (modifiedFields.length > 0) {
              changes.push({
                category: 'Flows',
                type: 'modified',
                name: item.name || 'Unnamed Flow',
                id: item.id,
                details: modifiedFields,
                diff: value
              })
            }
          }
        }
      })
    } else {
      // Handle case where entire array was replaced (e.g., from [] to [item])
      // This happens when jsondiffpatch doesn't use array diff format
      const currentFlows = currentConfig.rightCta?.offerFunnels || []
      const publishedFlows = publishedConfig.rightCta?.offerFunnels || []

      // Simple comparison - if lengths differ or items are different, report all current flows as added
      if (currentFlows.length > publishedFlows.length) {
        currentFlows.forEach((flow, idx) => {
          const existsInPublished = publishedFlows.some(pf => pf.id === flow.id)
          if (!existsInPublished) {
            changes.push({
              category: 'Flows',
              type: 'added',
              name: flow.name || 'Unnamed Flow',
              id: flow.id
            })
          }
        })
      }
    }

    return changes
  }

  const parseWidgetsChanges = (widgetsDiff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
    const changes: ConfigChange[] = []

    if (widgetsDiff._t === 'a') {
      Object.keys(widgetsDiff).forEach(key => {
        if (key === '_t') return

        const value = widgetsDiff[key]
        const index = parseInt(key.replace('_', ''))

        if (Array.isArray(value)) {
          if (value.length === 3 && value[1] === 0 && value[2] === 0) {
            const item = publishedConfig.rightCta?.widgets?.[index]
            if (item) {
              changes.push({
                category: 'Widgets',
                type: 'deleted',
                name: item.name || 'Unnamed Widget',
                id: item.id
              })
            }
          } else if (value.length === 1) {
            const item = currentConfig.rightCta?.widgets?.[index]
            if (item) {
              changes.push({
                category: 'Widgets',
                type: 'added',
                name: item.name || 'Unnamed Widget',
                id: item.id
              })
            }
          } else if (value.length === 2) {
            const item = currentConfig.rightCta?.widgets?.[index]
            if (item) {
              const modifiedFields = getModifiedFields(value[1])
              changes.push({
                category: 'Widgets',
                type: 'modified',
                name: item.name || 'Unnamed Widget',
                id: item.id,
                details: modifiedFields.length > 0 ? modifiedFields : undefined,
                diff: value
              })
            }
          }
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          const item = currentConfig.rightCta?.widgets?.[index]
          if (item) {
            const modifiedFields = getModifiedFields(value)
            if (modifiedFields.length > 0) {
              changes.push({
                category: 'Widgets',
                type: 'modified',
                name: item.name || 'Unnamed Widget',
                id: item.id,
                details: modifiedFields,
                diff: value
              })
            }
          }
        }
      })
    } else {
      const currentWidgets = currentConfig.rightCta?.widgets || []
      const publishedWidgets = publishedConfig.rightCta?.widgets || []

      if (currentWidgets.length > publishedWidgets.length) {
        currentWidgets.forEach((widget) => {
          const existsInPublished = publishedWidgets.some(pw => pw.id === widget.id)
          if (!existsInPublished) {
            changes.push({
              category: 'Widgets',
              type: 'added',
              name: widget.name || 'Unnamed Widget',
              id: widget.id
            })
          }
        })
      }
    }

    return changes
  }

  const parseGoalsChanges = (goalsDiff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
    const changes: ConfigChange[] = []

    if (goalsDiff._t === 'a') {
      Object.keys(goalsDiff).forEach(key => {
        if (key === '_t') return

        const value = goalsDiff[key]
        const index = parseInt(key.replace('_', ''))

        if (Array.isArray(value)) {
          if (value.length === 3 && value[1] === 0 && value[2] === 0) {
            const item = publishedConfig.goals?.[index]
            if (item) {
              changes.push({
                category: 'Goals',
                type: 'deleted',
                name: item.name || 'Unnamed Goal',
                id: item.id
              })
            }
          } else if (value.length === 1) {
            const item = currentConfig.goals?.[index]
            if (item) {
              changes.push({
                category: 'Goals',
                type: 'added',
                name: item.name || 'Unnamed Goal',
                id: item.id
              })
            }
          } else if (value.length === 2) {
            const item = currentConfig.goals?.[index]
            if (item) {
              const modifiedFields = getModifiedFields(value[1])
              changes.push({
                category: 'Goals',
                type: 'modified',
                name: item.name || 'Unnamed Goal',
                id: item.id,
                details: modifiedFields.length > 0 ? modifiedFields : undefined,
                diff: value
              })
            }
          }
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          const item = currentConfig.goals?.[index]
          if (item) {
            const modifiedFields = getModifiedFields(value)
            if (modifiedFields.length > 0) {
              changes.push({
                category: 'Goals',
                type: 'modified',
                name: item.name || 'Unnamed Goal',
                id: item.id,
                details: modifiedFields,
                diff: value
              })
            }
          }
        }
      })
    } else {
      const currentGoals = currentConfig.goals || []
      const publishedGoals = publishedConfig.goals || []

      if (currentGoals.length > publishedGoals.length) {
        currentGoals.forEach((goal) => {
          const existsInPublished = publishedGoals.some(pg => pg.id === goal.id)
          if (!existsInPublished) {
            changes.push({
              category: 'Goals',
              type: 'added',
              name: goal.name || 'Unnamed Goal',
              id: goal.id
            })
          }
        })
      }
    }

    return changes
  }

  const parsePhrasesChanges = (phrasesDiff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
    const changes: ConfigChange[] = []

    if (phrasesDiff._t === 'a') {
      Object.keys(phrasesDiff).forEach(key => {
        if (key === '_t') return

        const value = phrasesDiff[key]
        const index = parseInt(key.replace('_', ''))

        if (Array.isArray(value)) {
          if (value.length === 3 && value[1] === 0 && value[2] === 0) {
            const item = publishedConfig.phrases?.[index]
            if (item) {
              changes.push({
                category: 'Phrases',
                type: 'deleted',
                name: item.fallbackText || 'Unnamed Phrase',
                id: item.id
              })
            }
          } else if (value.length === 1) {
            const item = currentConfig.phrases?.[index]
            if (item) {
              changes.push({
                category: 'Phrases',
                type: 'added',
                name: item.fallbackText || 'Unnamed Phrase',
                id: item.id
              })
            }
          } else if (value.length === 2) {
            const item = currentConfig.phrases?.[index]
            if (item) {
              const modifiedFields = getModifiedFields(value[1])
              changes.push({
                category: 'Phrases',
                type: 'modified',
                name: item.fallbackText || 'Unnamed Phrase',
                id: item.id,
                details: modifiedFields.length > 0 ? modifiedFields : undefined,
                diff: value
              })
            }
          }
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          const item = currentConfig.phrases?.[index]
          if (item) {
            const modifiedFields = getModifiedFields(value)
            if (modifiedFields.length > 0) {
              changes.push({
                category: 'Phrases',
                type: 'modified',
                name: item.fallbackText || 'Unnamed Phrase',
                id: item.id,
                details: modifiedFields,
                diff: value
              })
            }
          }
        }
      })
    } else {
      const currentPhrases = currentConfig.phrases || []
      const publishedPhrases = publishedConfig.phrases || []

      if (currentPhrases.length > publishedPhrases.length) {
        currentPhrases.forEach((phrase) => {
          const existsInPublished = publishedPhrases.some(pp => pp.id === phrase.id)
          if (!existsInPublished) {
            changes.push({
              category: 'Phrases',
              type: 'added',
              name: phrase.fallbackText || 'Unnamed Phrase',
              id: phrase.id
            })
          }
        })
      }
    }

    return changes
  }

  const parseQuestionsChanges = (rightAskDiff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
    const changes: ConfigChange[] = []

    // Helper to check if a question has meaningful text
    const hasValidQuestionText = (question: any): boolean => {
      if (!question.text) return false
      const cleanText = stripHtml(question.text).trim()
      return cleanText.length > 0
    }

    // Check if there are changes to the questions array
    if (rightAskDiff.questions) {
      const currentQuestions = currentConfig.rightAsk?.questions || []
      const publishedQuestions = publishedConfig.rightAsk?.questions || []

      // Create maps for efficient ID-based lookup
      const currentById = new Map(currentQuestions.map(q => [q.id, q]))
      const publishedById = new Map(publishedQuestions.map(q => [q.id, q]))

      // Find added questions (but only if they have meaningful text)
      currentQuestions.forEach(question => {
        if (!publishedById.has(question.id) && hasValidQuestionText(question)) {
          changes.push({
            category: 'Questions',
            type: 'added',
            name: question.text || 'Unnamed Question',
            id: question.id
          })
        }
      })

      // Find deleted questions (but only if they had meaningful text)
      publishedQuestions.forEach(question => {
        if (!currentById.has(question.id) && hasValidQuestionText(question)) {
          changes.push({
            category: 'Questions',
            type: 'deleted',
            name: question.text || 'Unnamed Question',
            id: question.id
          })
        }
      })

      // Find modified questions (by comparing actual content, not array position)
      currentQuestions.forEach(currentQuestion => {
        const publishedQuestion = publishedById.get(currentQuestion.id)
        if (publishedQuestion) {
          // Compare the actual objects to see if there are real changes
          const diff = jsondiffpatch.diff(publishedQuestion, currentQuestion)
          if (diff) {
            const modifiedFields = getModifiedFields(diff)
            // Only show as a question change if it has meaningful text
            if (modifiedFields.length > 0 && hasValidQuestionText(currentQuestion)) {
              changes.push({
                category: 'Questions',
                type: 'modified',
                name: currentQuestion.text || 'Unnamed Question',
                id: currentQuestion.id,
                details: modifiedFields,
                diff: diff
              })
            }
          }
        }
      })
    }

    return changes
  }

  const parseAttributesChanges = (attributesDiff: any, currentConfig: Config, publishedConfig: Config): ConfigChange[] => {
    const changes: ConfigChange[] = []

    if (attributesDiff._t === 'a') {
      // Array diff format
      Object.keys(attributesDiff).forEach(key => {
        if (key === '_t') return

        const value = attributesDiff[key]
        const index = parseInt(key.replace('_', ''))

        if (Array.isArray(value)) {
          if (value.length === 3 && value[1] === 0 && value[2] === 0) {
            // Deleted
            const item = publishedConfig.attributes?.[index]
            if (item) {
              changes.push({
                category: 'Attribution',
                type: 'deleted',
                name: item.label || 'Unnamed Attribute',
                id: index.toString()
              })
            }
          } else if (value.length === 1) {
            // Added
            const item = currentConfig.attributes?.[index]
            if (item) {
              changes.push({
                category: 'Attribution',
                type: 'added',
                name: item.label || 'Unnamed Attribute',
                id: index.toString()
              })
            }
          } else if (value.length === 2) {
            // Modified
            const item = currentConfig.attributes?.[index]
            if (item) {
              const modifiedFields = getModifiedFields(value[1])
              changes.push({
                category: 'Attribution',
                type: 'modified',
                name: item.label || 'Unnamed Attribute',
                id: index.toString(),
                details: modifiedFields.length > 0 ? modifiedFields : undefined
              })
            }
          }
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          // Modified (nested object changes)
          const item = currentConfig.attributes?.[index]
          if (item) {
            const modifiedFields = getModifiedFields(value)
            if (modifiedFields.length > 0) {
              changes.push({
                category: 'Attribution',
                type: 'modified',
                name: item.label || 'Unnamed Attribute',
                id: index.toString(),
                details: modifiedFields
              })
            }
          }
        }
      })
    } else {
      // Non-array-diff format (entire array replaced)
      const currentAttributes = currentConfig.attributes || []
      const publishedAttributes = publishedConfig.attributes || []

      if (currentAttributes.length > publishedAttributes.length) {
        // Check for newly added attributes
        currentAttributes.forEach((attr, idx) => {
          if (idx >= publishedAttributes.length) {
            changes.push({
              category: 'Attribution',
              type: 'added',
              name: attr.label || 'Unnamed Attribute',
              id: idx.toString()
            })
          }
        })
      }
    }

    return changes
  }

  const formatChangeSummary = (changes: ConfigChange[]): string => {
    if (changes.length === 0) {
      return 'No changes detected'
    }

    const byCategory = changes.reduce((acc, change) => {
      if (!acc[change.category]) {
        acc[change.category] = { added: 0, modified: 0, deleted: 0, items: [] }
      }
      acc[change.category][change.type]++
      acc[change.category].items.push(change)
      return acc
    }, {} as Record<string, { added: number; modified: number; deleted: number; items: ConfigChange[] }>)

    const summary: string[] = []

    Object.entries(byCategory).forEach(([category, stats]) => {
      const parts: string[] = []
      if (stats.added > 0) parts.push(`${stats.added} added`)
      if (stats.modified > 0) parts.push(`${stats.modified} modified`)
      if (stats.deleted > 0) parts.push(`${stats.deleted} deleted`)

      summary.push(`${category}: ${parts.join(', ')}`)
      stats.items.forEach(item => {
        const icon = item.type === 'added' ? '✓' : item.type === 'modified' ? '~' : '✗'
        summary.push(`  ${icon} ${item.name}`)
        if (item.details && item.details.length > 0) {
          summary.push(`     (${item.details.join(', ')})`)
        }
      })
    })

    return summary.join('\n')
  }

  const generateCommitMessage = (changes: ConfigChange[]): string => {
    if (changes.length === 0) {
      return 'Update configuration'
    }

    const byCategory = changes.reduce((acc, change) => {
      if (!acc[change.category]) {
        acc[change.category] = { added: 0, modified: 0, deleted: 0, items: [] }
      }
      acc[change.category][change.type]++
      acc[change.category].items.push(change)
      return acc
    }, {} as Record<string, { added: number; modified: number; deleted: number; items: ConfigChange[] }>)

    // Generate a concise title
    const categories = Object.keys(byCategory)
    let title = ''

    if (categories.length === 1) {
      const category = categories[0]
      const stats = byCategory[category]
      if (stats.added > 0 && stats.modified === 0 && stats.deleted === 0) {
        title = `Add ${stats.added} ${category.toLowerCase()}`
      } else if (stats.modified > 0 && stats.added === 0 && stats.deleted === 0) {
        title = `Update ${stats.modified} ${category.toLowerCase()}`
      } else if (stats.deleted > 0 && stats.added === 0 && stats.modified === 0) {
        title = `Remove ${stats.deleted} ${category.toLowerCase()}`
      } else {
        title = `Update ${category.toLowerCase()}`
      }
    } else {
      title = `Update ${categories.length} configuration sections`
    }

    // Generate bullet points
    const bullets: string[] = []
    Object.entries(byCategory).forEach(([category, stats]) => {
      const parts: string[] = []
      if (stats.added > 0) parts.push(`${stats.added} added`)
      if (stats.modified > 0) parts.push(`${stats.modified} modified`)
      if (stats.deleted > 0) parts.push(`${stats.deleted} deleted`)

      bullets.push(`- ${category}: ${parts.join(', ')}`)

      // Add specific items for added/deleted (up to 3 per category)
      const itemsToList = stats.items.filter(i => i.type === 'added' || i.type === 'deleted').slice(0, 3)
      itemsToList.forEach(item => {
        bullets.push(`  - ${item.type === 'added' ? '✓' : '✗'} ${item.name}`)
      })
    })

    return `${title}\n\n${bullets.join('\n')}`
  }

  return {
    analyzeChanges,
    formatChangeSummary,
    generateCommitMessage,
    hasChanges,
    stripHtmlFromDiff
  }
}
